import { useState, useEffect } from 'react';
import { 
  Target, Shield, Car, Brain, Fuel, BookOpen, ArrowLeft, Loader
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_ICONS = {
  'Trafik Kuralları': Target,
  'Trafik Güvenliği': Shield,
  'Araç Bilgisi ve Manevra': Car,
  'Kişisel Koşullar': Brain,
  'Çevre': Fuel
};

const CATEGORY_COLORS = {
  'Trafik Kuralları': 'from-blue-500 to-blue-600',
  'Trafik Güvenliği': 'from-green-500 to-emerald-600',
  'Araç Bilgisi ve Manevra': 'from-purple-500 to-purple-600',
  'Kişisel Koşullar': 'from-orange-500 to-orange-600',
  'Çevre': 'from-teal-500 to-cyan-600'
};

export default function PracticeModeSelection({ onSelectCategory, onBack }) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [questionCount, setQuestionCount] = useState(10);

  useEffect(() => {
    fetchCategories();
    
    // Fetch quiz settings from backend
    const fetchQuizSettings = async () => {
      try {
        const response = await axios.get(`${API}/quiz-settings`);
        setQuestionCount(response.data.free_question_count || 10);
      } catch (error) {
        console.error('Quiz settings error:', error);
      }
    };
    
    fetchQuizSettings();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Kategoriler yüklenemedi:', error);
      alert('Kategoriler yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-950 via-red-800 to-red-900 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-16 h-16 text-white mx-auto mb-4 animate-spin" />
          <h2 className="text-2xl font-bold text-white">Kategoriler yükleniyor...</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-red-950 via-red-800 to-red-900">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute w-96 h-96 bg-red-500/20 rounded-full blur-3xl animate-blob top-0 left-0"></div>
        <div className="absolute w-96 h-96 bg-white/10 rounded-full blur-3xl animate-blob animation-delay-2000 top-0 right-0"></div>
        <div className="absolute w-96 h-96 bg-red-300/20 rounded-full blur-3xl animate-blob animation-delay-4000 bottom-0 left-1/2"></div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="mb-12">
          <button
            onClick={onBack}
            className="mb-6 flex items-center gap-2 text-white/80 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-semibold">Geri Dön</span>
          </button>
          
          <h1 className="text-5xl md:text-6xl font-black text-white mb-4 flex items-center gap-4">
            <BookOpen className="w-12 h-12 text-green-400" />
            📚 Pratik Modu
          </h1>
          <p className="text-white/80 text-xl">
            Bir kategori seçin ve pratik yapmaya başlayın. Her pratikta <span className="font-bold text-green-400">{questionCount} soru</span> çözeceksiniz.
          </p>
        </div>

        {/* Categories Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map((category) => {
            const Icon = CATEGORY_ICONS[category.name_turkish] || BookOpen;
            const colorClass = CATEGORY_COLORS[category.name_turkish] || 'from-gray-500 to-gray-600';
            
            return (
              <button
                key={category.id}
                onClick={() => onSelectCategory(category)}
                className="relative group"
              >
                <div className={`absolute -inset-1 bg-gradient-to-r ${colorClass} rounded-3xl blur opacity-25 group-hover:opacity-75 transition duration-300`}></div>
                <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20 hover:scale-105 transition-all duration-300 text-left">
                  <div className="flex flex-col items-center text-center">
                    <div className={`p-6 bg-gradient-to-r ${colorClass} rounded-2xl shadow-2xl mb-6`}>
                      <Icon className="w-16 h-16 text-white" />
                    </div>
                    <h3 className="text-2xl font-black text-white mb-3">
                      {category.name_turkish}
                    </h3>
                    <p className="text-white/70 text-sm mb-4">
                      {category.description_turkish || 'Kategori açıklaması'}
                    </p>
                    <div className="w-full pt-4 border-t border-white/20">
                      <div className="flex items-center justify-center gap-2 text-white/80">
                        <BookOpen className="w-5 h-5" />
                        <span className="font-semibold">10 Soru Çöz</span>
                      </div>
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Info Box */}
        <div className="mt-12 bg-gradient-to-r from-blue-500/20 to-purple-500/20 backdrop-blur-lg rounded-3xl p-8 border border-blue-500/30">
          <div className="flex items-start gap-4">
            <div className="p-4 bg-blue-500/30 rounded-full flex-shrink-0">
              <Target className="w-8 h-8 text-blue-300" />
            </div>
            <div>
              <h3 className="text-2xl font-black text-white mb-3">💡 Pratik İpuçları</h3>
              <ul className="text-white/80 space-y-2">
                <li>✅ Her kategoride düzenli pratik yapın</li>
                <li>✅ Zayıf olduğunuz kategorilere daha çok odaklanın</li>
                <li>✅ Açıklamaları dikkatlice okuyun</li>
                <li>✅ %74+ başarı oranı hedefleyin (gerçek sınav geçiş notu %74&apos;tür)</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
